import type { PickemQuestion } from '@low6-pickem/api-types';
import type { SelectionState } from '@/hooks/usePickemSelections';
import { QUESTION_TYPES, OVER_UNDER_QUESTION_TYPES } from '@/constants/question-types';

/**
 * Checks if a specific question has been answered
 *
 * Different question types have different answer criteria:
 * - Team/Player questions: Must have a team selected
 * - Prop questions: Must have 'yes' or 'no' selected
 * - Over/Under questions: Must have 'over' or 'under' selected
 * - Tiebreaker questions: Must have non-empty text input
 *
 * @param question - The question to check
 * @param selections - The current selection state
 * @returns True if the question has been answered, false otherwise
 *
 * @example
 * ```ts
 * const answered = isQuestionAnswered(question, selections);
 * if (answered) {
 *   // Question is complete
 * }
 * ```
 */
export function isQuestionAnswered(question: PickemQuestion, selections: SelectionState): boolean {
  const questionType = question.question_type?.toLowerCase();

  // Tiebreaker: Check if tiebreaker value is non-empty
  if (questionType === QUESTION_TYPES.TIEBREAKER) {
    return selections.tieBreaker.trim() !== '';
  }

  const questionId = question.question_id;

  // Prop question: Check if yes/no is selected
  if (questionType === QUESTION_TYPES.PROP) {
    return !!selections.props[questionId];
  }

  // Over/Under question (incl. match-level Total): Check if over/under is selected
  if (questionType && OVER_UNDER_QUESTION_TYPES.includes(questionType)) {
    return !!selections.overUnder[questionId];
  }

  // Everything else is an option pick (team/player/custom/leader/moneyline/
  // spread): check the teams bucket.
  return !!selections.teams[questionId];
}

/**
 * Maps an array of questions to a boolean array indicating which are answered
 *
 * Returns an array where each element corresponds to whether
 * the question at that index has been answered.
 *
 * @param questions - Array of questions to check
 * @param selections - The current selection state
 * @returns Boolean array where true = answered, false = not answered
 *
 * @example
 * ```ts
 * const answeredArray = getAnsweredQuestionsArray(questions, selections);
 * // [true, false, true, false, true] - Questions 0, 2, 4 are answered
 * ```
 */
export function getAnsweredQuestionsArray(
  questions: PickemQuestion[],
  selections: SelectionState
): boolean[] {
  return questions.map(question => isQuestionAnswered(question, selections));
}

/**
 * Counts how many questions have been answered
 *
 * @param questions - Array of questions to count
 * @param selections - The current selection state
 * @returns Number of questions that have been answered
 *
 * @example
 * ```ts
 * const count = countAnsweredQuestions(questions, selections);
 * console.log(`${count} out of ${questions.length} questions answered`);
 * ```
 */
export function countAnsweredQuestions(
  questions: PickemQuestion[],
  selections: SelectionState
): number {
  return questions.filter(q => isQuestionAnswered(q, selections)).length;
}

/**
 * Calculates the percentage of questions that have been answered
 *
 * Returns a value between 0 and 100 representing the completion percentage.
 * Returns 0 if there are no questions.
 *
 * @param questions - Array of questions
 * @param selections - The current selection state
 * @returns Percentage of answered questions (0-100)
 *
 * @example
 * ```ts
 * const progress = calculateProgressPercentage(questions, selections);
 * // 66.67 (if 2 out of 3 questions are answered)
 * ```
 */
export function calculateProgressPercentage(
  questions: PickemQuestion[],
  selections: SelectionState
): number {
  if (questions.length === 0) return 0;
  return (countAnsweredQuestions(questions, selections) / questions.length) * 100;
}

/**
 * Checks if all questions have been answered
 *
 * Returns true only if every question in the array has a valid answer.
 *
 * @param questions - Array of questions to check
 * @param selections - The current selection state
 * @returns True if all questions are answered, false otherwise
 *
 * @example
 * ```ts
 * const allComplete = areAllQuestionsAnswered(questions, selections);
 * if (allComplete) {
 *   // Enable submit button
 * }
 * ```
 */
export function areAllQuestionsAnswered(
  questions: PickemQuestion[],
  selections: SelectionState
): boolean {
  if (questions.length === 0) return false;
  return questions.every(q => isQuestionAnswered(q, selections));
}
